import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtErr } from "./api";
import { useAuth } from "./AuthContext";
import { Lock } from "lucide-react";

export default function Login({ role }) {
  const META = {
    admin: { title: "Office Admin Portal", subtitle: "Secure access for authorized BMS office staff.", home: "/portal", forgot: "/portal/forgot-password", denied: "This account does not have office admin access.", testid: "admin-login-page" },
    caregiver: { title: "Caregiver Portal", subtitle: "Secure access for BMS caregivers.", home: "/caregiver", forgot: "/caregiver/forgot-password", denied: "This account does not have caregiver portal access.", testid: "caregiver-login-page" },
    patient: { title: "Patient Portal", subtitle: "Secure access for BMS patients and their authorized representatives.", home: "/patient", forgot: "/patient/forgot-password", denied: "This account does not have patient portal access.", testid: "patient-login-page" },
  };
  const meta = META[role];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.role !== role) {
        await api.post("/auth/logout");
        setError(meta.denied);
        return;
      }
      setUser(data);
      navigate(meta.home);
    } catch (err) {
      setError(fmtErr(err, "Invalid email or password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={meta.testid} className="min-h-screen bg-brand-navy flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/assets/bms-logo.png" alt="BMS Health Care Services" className="h-20 w-auto mx-auto bg-white rounded-2xl p-2" />
          <h1 className="mt-6 font-display text-3xl text-white">{meta.title}</h1>
          <p className="mt-2 text-[15px] text-slate-300">{meta.subtitle}</p>
        </div>
        <form onSubmit={submit} className="rounded-2xl bg-white p-8 shadow-xl space-y-5">
          <div>
            <label htmlFor="login-email" className="block text-sm font-bold text-brand-navy">Email Address</label>
            <input
              id="login-email"
              data-testid="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-sm font-bold text-brand-navy">Password</label>
            <input
              id="login-password"
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal"
            />
          </div>
          {error && <p data-testid="login-error" className="text-sm font-semibold text-red-700" role="alert">{error}</p>}
          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal px-6 py-3.5 text-base font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95 disabled:opacity-60"
          >
            <Lock className="h-4 w-4" strokeWidth={1.8} />
            {loading ? "Signing in…" : "Sign In"}
          </button>
          <div className="flex items-center justify-between text-sm">
            <Link to={meta.forgot} data-testid="forgot-password-link" className="font-semibold text-brand-teal hover:text-brand-navy transition-colors duration-300">
              Forgot your password?
            </Link>
            <Link to="/" data-testid="back-to-site-link" className="font-semibold text-slate-500 hover:text-brand-navy transition-colors duration-300">
              Back to website
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
